import { formatCurrencyValue, normalizeCurrencyConfig } from './currencyFormat';
import { formatPercentageValue } from './percentageFormat';
import { appendWeightUnit, normalizeWeightConfig } from './weightFormat';
import { appendDistanceUnit, normalizeDistanceConfig } from './distanceFormat';

// Display metadata only: never use these settings to transform stored values.
export const NUMBER_FORMATS = [
  { value: 'number', label: 'Plain number' },
  { value: 'currency', label: 'Currency' },
  { value: 'weight', label: 'Weight' },
  { value: 'distance', label: 'Distance' },
  { value: 'percentage', label: 'Percentage' }
];

export const normalizeNumberDisplay = (config = {}) => {
  const display = {};
  if (config.format !== undefined) {
    if (!NUMBER_FORMATS.some(({ value }) => value === config.format)) {
      return { error: 'Select a valid number format.' };
    }
    display.format = config.format;
  }
  if (config.decimal_places !== undefined && config.decimal_places !== '') {
    const precision = Number(config.decimal_places);
    if (
      config.decimal_places === null ||
      typeof config.decimal_places === 'boolean' ||
      String(config.decimal_places).trim() === '' ||
      !Number.isInteger(precision) ||
      precision < 0 ||
      precision > 20
    ) {
      return {
        error: 'Decimal Places must be a whole number between 0 and 20.'
      };
    }
    display.decimal_places = precision;
  }
  const currency = normalizeCurrencyConfig(config);
  if (currency.error) return currency;
  const weight = normalizeWeightConfig(config);
  if (weight.error) return weight;
  const distance = normalizeDistanceConfig(config);
  if (distance.error) return distance;
  return {
    config: {
      ...display,
      ...currency.config,
      ...weight.config,
      ...distance.config
    }
  };
};

// Shared by tab values and heatmap labels. Omitting precision preserves
// legacy rendering. Currency and measurement formatting use explicit field settings.
export const formatNumberValue = (
  value,
  config = {},
  locale,
  { compact = false } = {}
) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value !== 'number' && typeof value !== 'string') return '-';
  if (typeof value === 'string' && !value.trim()) return '-';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  if (config?.format === 'percentage')
    return formatPercentageValue(numericValue, config, locale);
  if (config?.format === 'currency') {
    const formatted = formatCurrencyValue(numericValue, config, { compact });
    if (formatted !== null) return formatted;
  }
  if (compact) {
    const amount = new Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: 2
    }).format(numericValue);
    return appendDistanceUnit(appendWeightUnit(amount, config), config);
  }
  const precision = config?.decimal_places;
  if (!Number.isInteger(precision) || precision < 0 || precision > 20) {
    return appendDistanceUnit(appendWeightUnit(String(value), config), config);
  }
  const amount = new Intl.NumberFormat(locale, {
    useGrouping: false,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  }).format(numericValue);
  return appendDistanceUnit(appendWeightUnit(amount, config), config);
};
